/**
 * Invoice online payment link (#2809 / #2810).
 *
 * Hidden until Svea credentials are enabled (seller + secret + successful
 * test). Status verification lives on the server; this UI only creates,
 * re-sends and shows the link for copying (#2966).
 */

import { useTranslation } from 'react-i18next';
import { Button, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { formatDateTime } from '@/utils/locale';
import { CopyableCode } from '@/components/common/CopyableCode';
import { useNotify } from '@/hooks/useNotify';
import { translateBillingError } from '@/lib/billingErrors';
import { useSveaPaymentLinkStatus } from '@/hooks/useSveaIntegration';
import {
  useCreateSveaPayment,
  useSendSveaPayment,
  type InvoiceDetail,
  type SveaPaymentRequestStatus,
} from '@/hooks/useInvoices';

const STATUS_COLOR: Record<SveaPaymentRequestStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  created: 'info',
  paid: 'success',
  cancelled: 'default',
  error: 'error',
  expired: 'warning',
};

interface Props {
  invoice: InvoiceDetail;
  canManage: boolean;
}

export function SveaPaymentSection({ invoice, canManage }: Props) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const svea = useSveaPaymentLinkStatus(canManage);
  const create = useCreateSveaPayment();
  const send = useSendSveaPayment();

  const enabled = svea.data?.enabled === true;
  const open =
    invoice.invoiceType === 'invoice' &&
    (invoice.status === 'sent' || invoice.status === 'partial' || invoice.status === 'overdue');
  if (!canManage || !open) return null;
  if (!enabled) {
    return (
      <PaperBox>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('sveaPayment.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('sveaPayment.gated')}
        </Typography>
      </PaperBox>
    );
  }

  const request = invoice.sveaPaymentRequest ?? null;
  const canCreate = !request || request.status === 'cancelled' || request.status === 'error' || request.status === 'expired';
  const canSend = request?.status === 'created' && request.paymentUrl !== '';

  const handleCreate = async () => {
    try {
      await create.mutateAsync({ invoiceId: invoice.id });
      notify.success(t('sveaPayment.createSuccess'));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleSend = async (channel: 'email' | 'whatsapp') => {
    try {
      await send.mutateAsync({ invoiceId: invoice.id, channel });
      notify.success(t('sveaPayment.sendSuccess'));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  return (
    <PaperBox>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('sveaPayment.title')}
      </Typography>
      {request ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={STATUS_COLOR[request.status]}
              label={t(`sveaPayment.${request.status}`)}
              data-testid="svea-payment-status"
            />
            <Typography variant="body2">
              {t('sveaPayment.amount')}: {formatPrice(request.amountCents)}
            </Typography>
          </Stack>
          {request.sveaPaymentMethod ? (
            <Typography variant="caption" color="text.secondary">
              {t('sveaPayment.method')}: {request.sveaPaymentMethod}
            </Typography>
          ) : null}
          {request.lastSentAt && request.lastSentVia ? (
            <Typography variant="caption" color="text.secondary">
              {t('sveaPayment.sentVia')}: {t(`sveaPayment.channel${request.lastSentVia === 'email' ? 'Email' : 'Whatsapp'}`)}
              {request.lastSentTo ? ` · ${request.lastSentTo}` : ''}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      {canSend && request ? (
        // Copy as well as send (#2966): a customer at the counter, or one with
        // neither email nor WhatsApp, can only be reached by handing over the
        // link itself. Shown as selectable text so a blocked clipboard still
        // leaves a way to take it.
        <Stack sx={{ mb: 1.5 }}>
          <CopyableCode
            value={request.paymentUrl}
            copyLabel={t('sveaPayment.copyLink')}
            copiedLabel={t('sveaPayment.copied')}
            {...(request.expiresAt
              ? { helperText: t('sveaPayment.validUntil', { date: formatDateTime(request.expiresAt) }) }
              : {})}
          />
        </Stack>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {canCreate ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => void handleCreate()}
            disabled={create.isPending}
            data-testid="svea-payment-create"
          >
            {t('sveaPayment.create')}
          </Button>
        ) : null}
        {canSend ? (
          <>
            <Button
              size="small"
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => void handleSend('email')}
              disabled={send.isPending}
              data-testid="svea-payment-send-email"
            >
              {request?.lastSentVia ? t('sveaPayment.resend') : t('sveaPayment.sendEmail')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void handleSend('whatsapp')}
              disabled={send.isPending}
              data-testid="svea-payment-send-whatsapp"
            >
              {t('sveaPayment.sendWhatsapp')}
            </Button>
          </>
        ) : null}
      </Stack>
    </PaperBox>
  );
}

function PaperBox({ children }: { children: ReactNode }) {
  return <Paper sx={{ p: 3, mb: 3 }}>{children}</Paper>;
}
